import { 
  collection, 
  doc, 
  getDocs, 
  getDoc,
  getDocFromCache,
  getDocsFromCache,
  setDoc, 
  updateDoc,
  query,
  where,
  deleteDoc
} from 'firebase/firestore';
import { db } from '../config/firebase';

const COLLECTION_NAME = 'airports';

export const airportService = {
  // Sync all airports from backend
  async syncAirports() {
    try {
      const querySnapshot = await getDocs(collection(db, COLLECTION_NAME));
      const airports = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // The data will be automatically cached by Firestore's persistence
      return airports;
    } catch (error) {
      console.error('Error syncing airports:', error);
      throw error;
    }
  },

  // Fetch all airports (uses cache when offline, network when online)
  async getAllAirports() {
    try {
      const collectionRef = collection(db, COLLECTION_NAME);
      
      // If we're offline, use cache directly
      if (!navigator.onLine) {
        const querySnapshot = await getDocsFromCache(collectionRef);
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }

      // If we're online, try network first
      const querySnapshot = await getDocs(collectionRef);
      return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching airports:', error);
      
      // If network request failed, try cache as fallback
      try {
        const querySnapshot = await getDocsFromCache(collection(db, COLLECTION_NAME));
        return querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      } catch (cacheError) {
        console.error('Cache fallback failed:', cacheError);
        throw error; // throw original error if cache also fails
      }
    }
  },

  // Fetch a single airport by ID (uses cache when offline, network when online)
  async getAirportById(airportCode) {
    try {
      const docRef = doc(db, COLLECTION_NAME, airportCode);
      
      // If we're offline, use cache directly
      if (!navigator.onLine) {
        const docSnap = await getDocFromCache(docRef);
        if (docSnap.exists()) {
          return {
            id: docSnap.id,
            ...docSnap.data()
          };
        }
      }

      // If we're online, try network first
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        return {
          id: docSnap.id,
          ...docSnap.data()
        };
      }

      throw new Error('Airport not found');
    } catch (error) {
      console.error('Error fetching airport:', error);
      
      // If network request failed, try cache as fallback
      if (navigator.onLine) {  // Only try cache fallback if we were trying network
        try {
          const docSnap = await getDocFromCache(docRef);
          if (docSnap.exists()) {
            return {
              id: docSnap.id,
              ...docSnap.data()
            };
          }
        } catch (cacheError) {
          console.error('Cache fallback failed:', cacheError);
        }
      }
      
      throw error;
    }
  },

  // Create or update an airport
  async saveAirport(airportData) {
    try {
      const { airportCode, ...data } = airportData;
      if (!airportCode) {
        throw new Error('Airport code is required');
      }
      
      const docRef = doc(db, COLLECTION_NAME, airportCode);
      await setDoc(docRef, {
        ...data,
        lastModified: new Date().toISOString()
      }, { merge: true });

      return airportCode;
    } catch (error) {
      console.error('Error saving airport:', error);
      throw error;
    }
  },

  // Update airport operational considerations
  async updateOperationalConsiderations(airportCode, operationalConsiderations, timestamp) {
    try {
      const docRef = doc(db, COLLECTION_NAME, airportCode);
      await updateDoc(docRef, {
        operationalConsiderations,
        operationalLastModified: timestamp,
        dateModified: timestamp,
        lastModified: timestamp
      });
    } catch (error) {
      console.error('Error updating operational considerations:', error);
      throw error;
    }
  },

  // Update airport secret information
  async updateSecretInformation(airportCode, secretInformation, timestamp) {
    try {
      const docRef = doc(db, COLLECTION_NAME, airportCode);
      await updateDoc(docRef, {
        secretInformation,
        secretLastModified: timestamp,
        dateModified: timestamp,
        lastModified: timestamp
      });
    } catch (error) {
      console.error('Error updating secret information:', error);
      throw error;
    }
  },

  // Update ground time and logistics information
  async updateGroundTimeAndLogistics(airportCode, groundTimeAndLogistics, timestamp) {
    try {
      const docRef = doc(db, COLLECTION_NAME, airportCode);
      await updateDoc(docRef, {
        groundTimeAndLogistics,
        logisticsLastModified: timestamp,
        dateModified: timestamp,
        lastModified: timestamp
      });
    } catch (error) {
      console.error('Error updating ground time and logistics information:', error);
      throw error;
    }
  },

  // Update airport dogs and fun information
  async updateDogsAndFun(airportCode, dogsAndFun, timestamp) {
    try {
      const docRef = doc(db, COLLECTION_NAME, airportCode);
      await updateDoc(docRef, {
        dogsAndFun,
        dogsLastModified: timestamp,
        dateModified: timestamp,
        lastModified: timestamp
      });
    } catch (error) {
      console.error('Error updating dogs and fun information:', error);
      throw error;
    }
  },

  // Update airport services
  async updateServices(airportCode, services) {
    try {
      const docRef = doc(db, COLLECTION_NAME, airportCode);
      await updateDoc(docRef, {
        services,
        servicesLastModified: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating services:', error);
      throw error;
    }
  },

  // Get special use airports (now using tags)
  async getSpecialUseAirports() {
    try {
      // Fetch all airports since we need to filter by tag contents
      const querySnapshot = await getDocs(collection(db, COLLECTION_NAME));
      
      // Filter airports that have specialuse or nightrestricted tags
      return querySnapshot.docs
        .map(doc => ({
          id: doc.id,
          ...doc.data()
        }))
        .filter(airport => {
          const tags = (airport.tags || []).map(tag => 
            tag.toLowerCase().replace(/#/g, '')
          );
          return tags.includes('specialuse') || tags.includes('nightrestricted');
        });
    } catch (error) {
      console.error('Error fetching special use airports:', error);
      throw error;
    }
  },

  // Delete an airport
  async deleteAirport(airportCode) {
    try {
      if (!airportCode) {
        throw new Error('Airport code is required');
      }
      
      const docRef = doc(db, COLLECTION_NAME, airportCode);
      await deleteDoc(docRef);
    } catch (error) {
      console.error('Error deleting airport:', error);
      throw error;
    }
  },

  // Update base maps
  async updateBaseMaps(airportCode, baseMaps) {
    try {
      const docRef = doc(db, COLLECTION_NAME, airportCode);
      await updateDoc(docRef, {
        baseMaps,
        baseMapsLastModified: new Date().toISOString(),
        dateModified: new Date().toISOString(),
        lastModified: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating base maps:', error);
      throw error;
    }
  },

  // Add a base map
  async addBaseMap(airportCode, baseMap) {
    try {
      const docRef = doc(db, COLLECTION_NAME, airportCode);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error('Airport not found');
      }

      const data = docSnap.data();
      const baseMaps = data.baseMaps || [];
      baseMaps.push(baseMap);

      await updateDoc(docRef, {
        baseMaps,
        baseMapsLastModified: new Date().toISOString(),
        dateModified: new Date().toISOString(),
        lastModified: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error adding base map:', error);
      throw error;
    }
  },

  // Delete a base map
  async deleteBaseMap(airportCode, baseMapId) {
    try {
      const docRef = doc(db, COLLECTION_NAME, airportCode);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) {
        throw new Error('Airport not found');
      }

      const data = docSnap.data();
      const baseMaps = (data.baseMaps || []).filter(map => map.id !== baseMapId);

      await updateDoc(docRef, {
        baseMaps,
        baseMapsLastModified: new Date().toISOString(),
        dateModified: new Date().toISOString(),
        lastModified: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error deleting base map:', error);
      throw error;
    }
  },

  // Update airport tags
  async updateTags(airportCode, tags) {
    try {
      const docRef = doc(db, COLLECTION_NAME, airportCode);
      await updateDoc(docRef, {
        tags,
        tagsLastModified: new Date().toISOString(),
        dateModified: new Date().toISOString(),
        lastModified: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error updating tags:', error);
      throw error;
    }
  }
}; 